import React from 'react';
import { Box, Card, CardContent, Typography, Chip } from '@mui/material';

function EquipmentStatusCard({ title, icon, connected, data, children, equipmentName, compact = false, cardSx, stale = false, lastReportedControl = null }) {
  return (
    <Card
      // Stay on the elevation variant even in compact mode so the MUI
      // theme's paper backdrop (the same gray the Operations view's
      // Observatory tab shows) is applied. A subtle explicit border
      // keeps neighboring compact cards visually distinct when two or
      // more share a row.
      //
      // `stale` tints the card amber when it is showing last-reported
      // (snapshot) values for a device that is currently disconnected.
      sx={{
        height: '100%',
        ...(compact && {
          border: '1px solid rgba(255,255,255,0.18)',
        }),
        ...(stale && {
          bgcolor: 'rgba(255,193,7,0.13)',
          border: '1px solid',
          borderColor: 'warning.main',
        }),
        ...cardSx,
      }}
    >
      <CardContent sx={compact ? { p: 1.25, '&:last-child': { pb: 1.25 } } : undefined}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: compact ? 1 : 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {icon}
            <Box>
              <Typography variant={compact ? 'subtitle2' : 'h6'} sx={compact ? { fontWeight: 600, lineHeight: 1.2 } : undefined}>{title}</Typography>
              {equipmentName ? (
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                  {equipmentName}
                </Typography>
              ) : (
                connected && (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    No name available
                  </Typography>
                )
              )}
            </Box>
          </Box>
          <Chip
            label={stale ? 'Last reported' : connected ? 'Connected' : 'Disconnected'}
            color={stale ? 'warning' : connected ? 'success' : 'default'}
            size="small"
          />
        </Box>
        {connected && children}
        {!connected && (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            No data available - equipment not connected or not configured
          </Typography>
        )}
        {/* "Last Reported Status" toggle (rendered by the parent) — shown only
            when the device is disconnected but a snapshot exists. */}
        {lastReportedControl}
      </CardContent>
    </Card>
  );
}

export default EquipmentStatusCard;

// Helper: format RA/Dec from decimal to sexagesimal
export function decimalToSexagesimal(decimal, isRA = false) {
  if (decimal === null || decimal === undefined) return 'N/A';
  const absolute = Math.abs(decimal);
  if (isRA) {
    const hours = absolute;
    const h = Math.floor(hours);
    const m = Math.floor((hours - h) * 60);
    const s = ((hours - h) * 60 - m) * 60;
    return `${h.toString().padStart(2, '0')}h ${m.toString().padStart(2, '0')}m ${s.toFixed(2).padStart(5, '0')}s`;
  } else {
    const sign = decimal < 0 ? '-' : '+';
    const d = Math.floor(absolute);
    const m = Math.floor((absolute - d) * 60);
    const s = ((absolute - d) * 60 - m) * 60;
    return `${sign}${d.toString().padStart(2, '0')}° ${m.toString().padStart(2, '0')}' ${s.toFixed(1).padStart(4, '0')}"`;
  }
}

// Helper: format tracking mode
export function formatTrackingMode(trackingMode, customRaRate, customDecRate) {
  if (!trackingMode) return 'Unknown';
  if (trackingMode === 'Custom' && (customRaRate !== undefined || customDecRate !== undefined)) {
    const raStr = customRaRate !== undefined ? `RA: ${customRaRate.toFixed(3)}` : '';
    const decStr = customDecRate !== undefined ? `Dec: ${customDecRate.toFixed(3)}` : '';
    const rates = [raStr, decStr].filter(s => s).join(', ');
    return `Custom (${rates})`;
  }
  return trackingMode;
}
